import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BarChart3, Menu, X, User, LogOut, Settings, Crown, CreditCard, Plus, FileText, HelpCircle, Infinity, Clock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import Button from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/utils/cn';
import { metaPixel } from '@/utils/meta-pixel';

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, logout, isAuthenticated, refreshUserCredits } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      refreshUserCredits();
    }
  }, [isAuthenticated]);

  const isPro = window.location.hostname === 'pro.dpewizard.com';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Navigation publique
  const publicNavigation = [
    { name: 'Accueil', href: '/' },
    { name: 'Tarifs', href: '/pricing' },
    { name: 'À propos', href: '/about' },
    { name: 'Contact', href: '/contact' }
  ];

  // Navigation utilisateur connecté
  const userNavigation = [
    { name: 'Tableau de bord', href: '/dashboard', icon: BarChart3 },
    { name: 'Nouvelle analyse', href: '/analysis/new', icon: Plus },
    { name: 'Mes analyses', href: '/analysis/list', icon: FileText },
    { name: 'Mon profil', href: '/profile', icon: User },
    { name: 'Abonnement', href: '/pricing', icon: Crown },
    { name: 'Facturation', href: '/billing', icon: CreditCard },
    { name: 'Support', href: '/contact', icon: HelpCircle }
  ];

  // Vérifier si l'utilisateur a un abonnement illimité
  const hasUnlimitedCredits = user?.subscription_status === 'active' || user?.subscription_status === 'trialing';

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-green-600 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">
              DPEWizard
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {publicNavigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(item.href);
                }}
                className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors"
              >
                {item.name}
              </a>
            ))}
            
            {/* Analyses link for authenticated users */}
            {isAuthenticated && (
              <a
                key="analyses"
                href="/dashboard"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/dashboard');
                }}
                className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors"
              >
                Analyses
              </a>
            )}
          </nav>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                {/* Credits display */}
                <div className="flex items-center space-x-2 text-sm">
                  <CreditCard className="w-4 h-4 text-gray-500" />
                  {hasUnlimitedCredits ? (
                    <div className="flex items-center text-green-600">
                      <Infinity className="w-4 h-4 mr-1" />
                      <span className="font-medium">Illimité</span>
                    </div>
                  ) : (
                    <span className="text-gray-700">
                      <span className="font-medium">{user?.credits || 0}</span> crédits
                    </span>
                  )}
                </div>
                
                {/* User menu */}
                <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">
                    {user?.email?.split('@')[0]}
                  </span>
                </button>

                {/* User Dropdown */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    {userNavigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <item.icon className="w-4 h-4 mr-3" />
                        {item.name}
                      </Link>
                    ))}
                    <hr className="my-1" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Déconnexion
                    </button>
                  </div>
                )}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/auth')}
                >
                  Connexion
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate(isPro ? '/auth?mode=signup' : '/pricing')}
                >
                  Commencer
                </Button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-600 hover:text-gray-900 p-2"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            {/* Credits display on mobile */}
            {isAuthenticated && (
              <div className="px-3 py-2 mb-4 bg-gray-50 rounded-lg mx-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Crédits disponibles</span>
                  {hasUnlimitedCredits ? (
                    <div className="flex items-center text-green-600">
                      <Infinity className="w-4 h-4 mr-1" />
                      <span className="font-medium text-sm">Illimité</span>
                    </div>
                  ) : (
                    <span className="font-medium text-sm text-gray-900">
                      {user?.credits || 0}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            <nav className="space-y-2">
              {/* Public navigation */}
              {publicNavigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.href);
                    setIsMenuOpen(false);
                  }}
                  className="block px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                >
                  {item.name}
                </a>
              ))}
              
              {/* User navigation for authenticated users */}
              {isAuthenticated && (
                <>
                  <hr className="my-2 border-gray-200" />
                  {userNavigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <item.icon className="w-4 h-4 mr-3" />
                      {item.name}
                    </Link>
                  ))}
                </>
              )}
            </nav>

            {/* Mobile Auth */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              {isAuthenticated ? (
                <div className="space-y-2">
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Connecté en tant que {user?.email}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 rounded-md"
                  >
                    Déconnexion
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Button
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={() => {
                      navigate('/auth');
                      setIsMenuOpen(false);
                    }}
                  >
                    Connexion
                  </Button>
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={() => {
                      metaPixel.trackPricingCTA();
                      navigate(isPro ? '/auth?mode=signup' : '/pricing');
                      setIsMenuOpen(false);
                    }}
                  >
                    Commencer
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Overlay for user dropdown */}
      {isUserMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsUserMenuOpen(false)}
        />
      )}
    </header>
  );
};

export default Header;